import React from 'react';
import { X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { MoneyInput } from '../../../components/MoneyInput';

/**
 * Generic repeatable "type + description + amount" list section used by
 * Investment Accounts, Other Assets, Insurance, Liabilities, and Other Income.
 */
export default function TypedList({
  title,
  items,
  onChange,
  typeOptions,
  amountLabel = 'Amount',
  amountPlaceholder = '$0',
  itemLabel = 'Item',
  testIdPrefix,
  subCategoryMap, // optional: { parentValue: [{value,label}, ...] }
}) {
  const list = items || [];
  const add = () => onChange([...list, { type: '', subCategory: '', description: '', amount: '' }]);
  const remove = (idx) => onChange(list.filter((_, i) => i !== idx));
  const update = (idx, field, value) => onChange(list.map((e, i) => (i === idx ? { ...e, [field]: value, ...(field === 'type' ? { subCategory: '' } : {}) } : e)));

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-[#0f172a]">{title}</h3>
        <Button onClick={add} size="sm" variant="outline" data-testid={`add-${testIdPrefix}-button`}>
          <span className="text-lg mr-1">+</span> Add
        </Button>
      </div>
      {list.map((item, idx) => {
        const subOptions = subCategoryMap && item.type ? subCategoryMap[item.type] : null;
        return (
          <div key={idx} className="p-4 bg-[#f8fafc] rounded-md border border-[#e2e8f0] mb-4">
            <div className="flex justify-between items-start mb-4">
              <span className="text-sm font-medium text-[#475569]">{itemLabel} #{idx + 1}</span>
              <Button onClick={() => remove(idx)} size="sm" variant="ghost" data-testid={`remove-${testIdPrefix}-${idx}`}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className={`grid grid-cols-1 md:grid-cols-${subOptions ? 4 : 3} gap-4`}>
              <div>
                <Label>Type</Label>
                <Select value={item.type || ''} onValueChange={(v) => update(idx, 'type', v)}>
                  <SelectTrigger className="mt-2" data-testid={`${testIdPrefix}-type-${idx}`}><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {typeOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {subOptions && (
                <div>
                  <Label>Sub-category</Label>
                  <Select value={item.subCategory || ''} onValueChange={(v) => update(idx, 'subCategory', v)}>
                    <SelectTrigger className="mt-2" data-testid={`${testIdPrefix}-sub-${idx}`}><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {subOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Description</Label>
                <Input placeholder="Description" value={item.description || ''} onChange={(e) => update(idx, 'description', e.target.value)} className="mt-2" />
              </div>
              <div>
                <Label>{amountLabel}</Label>
                <MoneyInput placeholder={amountPlaceholder} value={item.amount} onChange={(v) => update(idx, 'amount', v)} className="mt-2" />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
